import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createQuestionNote,
  createQuestionReport,
  deleteQuestionNote,
  deleteQuestionReport,
  fetchQuestionNotes,
  fetchQuestionReports,
  fetchQuestionViewActivity,
} from "../services/questionBankQuestionService";

function normalizeReport(item = {}, index = 0) {
  return {
    id: item?.report_id || item?.id || index + 1,
    description: item?.description || "",
    replyDescription: item?.reply_description || "",
  };
}

function normalizeNote(item = {}, index = 0) {
  return {
    id: item?.note_id || item?.id || index + 1,
    description: item?.description || "",
  };
}

function normalizeViewFeedItem(item = {}, index = 0) {
  const type = String(item?.type || "").toLowerCase();
  const normalizedType =
    type === "like"
      ? "Like"
      : type === "view"
        ? "View"
        : type === "report"
          ? "Report"
          : type === "note" || type === "notes"
            ? "Notes"
            : "All";

  return {
    id: item?.user_id || item?.id || index + 1,
    name: item?.name || item?.user_name || "User",
    avatar: item?.profile_pics || item?.profile_pic || item?.avatar || "",
    time: item?.updated_at || item?.created_at || "",
    type: normalizedType,
    text:
      item?.description ||
      (normalizedType === "Like"
        ? "Liked this question"
        : normalizedType === "View"
          ? "Viewed this question"
          : normalizedType === "Report"
            ? "Reported an issue"
            : normalizedType === "Notes"
              ? "Added a note"
              : "Activity"),
  };
}

function updateQuestionMetrics(currentData, questionId, updater) {
  if (!currentData?.questions?.length) {
    return currentData;
  }

  return {
    ...currentData,
    questions: currentData.questions.map((question) =>
      Number(question?.id) === Number(questionId) ? updater(question) : question
    ),
  };
}

function toSafeReportCount(value) {
  const normalizedValue = Number(value);

  return Number.isFinite(normalizedValue) ? normalizedValue : 0;
}

export function useQuestionReports(questionId, options = {}) {
  return useQuery({
    queryKey: ["questionReports", Number(questionId)],
    queryFn: async () => {
      const { payload } = await fetchQuestionReports({
        question_id: Number(questionId),
      });

      return {
        totalReportsLabel: payload?.total_reports || "0",
        isBold: Number(payload?.is_bold || 0) === 1,
        items: (Array.isArray(payload?.user_reports) ? payload.user_reports : []).map(normalizeReport),
      };
    },
    enabled: Boolean(questionId) && (options.enabled ?? true),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: {
      totalReportsLabel: "0",
      isBold: false,
      items: [],
    },
  });
}

export function useQuestionNotes(questionId, options = {}) {
  return useQuery({
    queryKey: ["questionNotes", Number(questionId)],
    queryFn: async () => {
      const { payload } = await fetchQuestionNotes({
        question_id: Number(questionId),
      });

      return {
        totalNotes: Number(payload?.total_notes || 0),
        items: (Array.isArray(payload?.user_notes) ? payload.user_notes : []).map(normalizeNote),
      };
    },
    enabled: Boolean(questionId) && (options.enabled ?? true),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: {
      totalNotes: 0,
      items: [],
    },
  });
}

export function useQuestionViewActivity(questionId, options = {}) {
  return useQuery({
    queryKey: ["questionViewActivity", Number(questionId)],
    queryFn: async () => {
      const { payload } = await fetchQuestionViewActivity({
        question_id: Number(questionId),
      });

      const allItems = Array.isArray(payload?.all)
        ? payload.all.map(normalizeViewFeedItem)
        : [];
      const likeItems = Array.isArray(payload?.likes)
        ? payload.likes.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "Like");
      const viewItems = Array.isArray(payload?.views)
        ? payload.views.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "View");
      const reportItems = Array.isArray(payload?.reports)
        ? payload.reports.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "Report");
      const notesItems = Array.isArray(payload?.notes)
        ? payload.notes.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "Notes");

      return {
        totalData: Number(payload?.total_data || allItems.length || 0),
        totalLikes: Number(payload?.total_likes || likeItems.length || 0),
        totalViews: Number(payload?.total_views || viewItems.length || 0),
        totalNotes: Number(payload?.total_notes || notesItems.length || 0),
        totalReports: Number(payload?.total_reports || reportItems.length || 0),
        all: allItems,
        like: likeItems,
        view: viewItems,
        report: reportItems,
        notes: notesItems,
      };
    },
    enabled: Boolean(questionId) && (options.enabled ?? true),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: {
      totalData: 0,
      totalLikes: 0,
      totalViews: 0,
      totalNotes: 0,
      totalReports: 0,
      all: [],
      like: [],
      view: [],
      report: [],
      notes: [],
    },
  });
}

export function useQuestionBankQuestionFeedback(questionQueryParams = {}) {
  const queryClient = useQueryClient();
  const questionListQueryKey = ["questionBankQuestions", questionQueryParams];

  const createReport = useMutation({
    mutationFn: createQuestionReport,
    onSuccess: (_result, variables) => {
      const questionId = Number(variables?.question_id);

      queryClient.invalidateQueries({ queryKey: ["questionReports", questionId] });
      queryClient.setQueryData(questionListQueryKey, (currentData) =>
        updateQuestionMetrics(currentData, questionId, (question) => ({
          ...question,
          reportCount: String(toSafeReportCount(question.reportCount) + 1),
          reportStatus: true,
        }))
      );
    },
  });

  const deleteReport = useMutation({
    mutationFn: deleteQuestionReport,
    onSuccess: (_result, variables) => {
      const questionId = Number(variables?.question_id);

      queryClient.invalidateQueries({ queryKey: ["questionReports", questionId] });
      queryClient.setQueryData(questionListQueryKey, (currentData) =>
        updateQuestionMetrics(currentData, questionId, (question) => {
          const nextReportCount = Math.max(0, toSafeReportCount(question.reportCount) - 1);

          return {
            ...question,
            reportCount: String(nextReportCount),
            reportStatus: nextReportCount > 0,
          };
        })
      );
    },
  });

  const createNote = useMutation({
    mutationFn: createQuestionNote,
    onSuccess: (_result, variables) => {
      const questionId = Number(variables?.question_id);

      queryClient.invalidateQueries({ queryKey: ["questionNotes", questionId] });
      queryClient.setQueryData(questionListQueryKey, (currentData) =>
        updateQuestionMetrics(currentData, questionId, (question) => ({
          ...question,
          notesCount: Number(question.notesCount || 0) + 1,
        }))
      );
    },
  });

  const deleteNote = useMutation({
    mutationFn: deleteQuestionNote,
    onSuccess: (_result, variables) => {
      const questionId = Number(variables?.question_id);

      queryClient.invalidateQueries({ queryKey: ["questionNotes", questionId] });
      queryClient.setQueryData(questionListQueryKey, (currentData) =>
        updateQuestionMetrics(currentData, questionId, (question) => ({
          ...question,
          notesCount: Math.max(0, Number(question.notesCount || 0) - 1),
        }))
      );
    },
  });

  return {
    createReport,
    deleteReport,
    createNote,
    deleteNote,
  };
}
